import { useCallback, useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetHeaderLeftIcon,
  SheetTitle,
  SheetCustomContent,
  SheetFooter,
  InputContainer,
  Input,
  InputBase,
} from '@sweetspot/scramble-ds'
import { Trans, useTranslation } from 'react-i18next'
import { useToasts } from 'react-toast-notifications'
import { Button } from '@sweetspot/scramble-ds/atoms'
import { ArchiveResourcePayload, archiveResource } from '@sweetspot/shared/data-access/api-platform'
import { useMutation } from 'react-query'
import { SideBarMode } from '../../../../../types'
import { useResourcesStore } from '../../../../../hooks/resources/useResourcesStore'
import { useIsMobile } from '../../../../../hooks/useIsMobile'

const ArchiveResources = () => {
  const { t } = useTranslation()
  const { addToast } = useToasts()
  const [userInput, setUserInput] = useState('')
  const {
    selectedResource,
    toggleSideBar,
    archiveResource: archiveResourceInStore,
  } = useResourcesStore()

  const isMobile = useIsMobile()
  const toggleArchiveResourceSideBar = useCallback(() => {
    toggleSideBar(SideBarMode.ARCHIVE_RESOURCE)
  }, [toggleSideBar])

  const archiveResourceMutation = useMutation(
    (data: ArchiveResourcePayload) => archiveResource(data),
    {
      onSuccess: () => {
        archiveResourceInStore(selectedResource?.resourceId || '')
        addToast(t('sentences.resourceArchivedSuccess'), { appearance: 'success' })
        toggleArchiveResourceSideBar()
      },
      onError: () => {
        addToast(t('errors.somethingWentWrongNoId'), { appearance: 'error' })
      },
    }
  )

  const onSubmit = () => {
    if (!selectedResource) return
    archiveResourceMutation.mutate({ resourceId: selectedResource.resourceId })
  }

  const requiredInputText = selectedResource?.name || ''
  const isAllowedToArchive = requiredInputText === userInput

  return (
    <Sheet open onOpenChange={toggleArchiveResourceSideBar}>
      <SheetContent
        side={isMobile ? 'bottom' : 'right'}
        className="h-auto rounded-b-none rounded-t-xl md:rounded-none"
      >
        <SheetHeader className="rounded-b-none rounded-t-xl md:rounded-none">
          <SheetHeaderLeftIcon onClick={toggleArchiveResourceSideBar}>
            <i className="fa-regular fa-close" />
          </SheetHeaderLeftIcon>
          <SheetTitle>
            {t('sentences.archiveResource')}: {selectedResource?.name || ''}
          </SheetTitle>
        </SheetHeader>
        <SheetCustomContent>
          <p className="text-content-base text-text-dark font-regular mb-4">
            {t('sentences.archiveResourceDescription')}
            <br />
            <br />
            <Trans
              i18nKey="sentences.archiveResourceInputLabel"
              values={{ inputText: requiredInputText }}
            />
          </p>
          <InputBase>
            <InputContainer>
              <Input
                rounding="rectangular"
                type="text"
                className="text-content-base text-text-dark font-regular"
                onChange={(e) => setUserInput(e.target.value)}
              />
            </InputContainer>
          </InputBase>
        </SheetCustomContent>
        <SheetFooter className="pt-0">
          <Button
            className="bg-surface-danger w-full"
            variant="primary"
            disabled={!isAllowedToArchive}
            onClick={onSubmit}
          >
            {t('sentences.archiveResource')}
          </Button>
          <Button onClick={toggleArchiveResourceSideBar} className="w-full" variant="clear-dark">
            {t('cancel')}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}

export default ArchiveResources
